/**
 * Reviews — Weidner Classic
 *
 * Tower 12 resident-reviews feed (https://www.tower12.com/resident-reviews),
 * under the shared HeroSolidBG "REVIEWS" banner + property_reviews_blurb:
 *
 *   1. Centered feed heading ("What our customers say")
 *   2. Google summary bar: Google Reviews mark, average rating, yellow
 *      stars, review count, blue "Review us on Google" CTA
 *   3. Vertical feed of review cards: letter avatar, full name, relative
 *      date, yellow stars, truncated body with Read more, "Posted on Google"
 *
 * Driven by the `reviews` collection (same binding as the rise parent).
 * The rise 3-column bordered card grid and name abbreviation are dropped.
 * Interior hero + intro blurb stay on HeroSolidBG in pages.json.
 *
 * Maintenance checklist when re-syncing with the rise parent:
 *   1. Diff this file against the rise Reviews in the sibling checkout.
 *   2. Do not reintroduce the 3-column grid or abbreviated reviewer names.
 *   3. Keep the summary bar + vertical Google-style feed layout.
 *   4. Run `pnpm run lint:schema` and `pnpm run typecheck`.
 */

import { useMemo, useState } from 'react';
import type { ComponentContentSchema, SlotGroupDefinition } from '@vibesites/runtime';
import {
  useCollectionBinding,
  useContentToken,
  useSlotGroup,
  EditableToken,
  EditableSlotGroup,
  EditableCollectionBinding,
} from '@vibesites/runtime/react';
import { useScrollReveal } from '../hooks/useScrollReveal';

interface Review {
  name?: string | null;
  title?: string | null;
  rating?: string | number | null;
  text?: string | null;
  date?: string | null;
  source?: string | null;
}

const TRUNCATE_LENGTH = 280;
const STAR_YELLOW = '#FBBC04';
const CTA_BLUE = '#1a73e8';
const AVATAR_PALETTE = [
  '#E57373',
  '#F06292',
  '#BA68C8',
  '#7986CB',
  '#64B5F6',
  '#4DB6AC',
  '#81C784',
  '#FFB74D',
  '#A1887F',
  '#90A4AE',
];

const googleCtaDef: SlotGroupDefinition = {
  label: 'Review Us on Google',
  fields: {
    text: {
      label: 'Button Text',
      type: 'text',
      defaultValue: 'Review us on Google',
    },
    url: {
      label: 'URL',
      type: 'url',
      defaultField: 'property_google_business_url',
      suggestedFields: ['property_google_business_url', 'property_google_place_url'],
    },
    _target: { label: 'Target', type: 'text', defaultValue: '_blank' },
  },
};

function hasValue(val: unknown): val is string {
  if (val == null) return false;
  const s = String(val).trim().toLowerCase();
  return s !== '' && s !== 'false' && s !== 'null' && s !== 'undefined';
}

function parseRating(rating: string | number | null | undefined): number {
  if (rating == null) return 5;
  const n = typeof rating === 'number' ? rating : parseFloat(String(rating));
  if (Number.isNaN(n)) return 5;
  return Math.min(5, Math.max(0, n));
}

function stripHtml(html: string): string {
  return html
    .replace(/<br\s*\/?>/gi, '\n')
    .replace(/<\/p>/gi, '\n')
    .replace(/<[^>]+>/g, '')
    .replace(/&nbsp;/g, ' ')
    .replace(/&amp;/g, '&')
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/&quot;/g, '"')
    .replace(/&#39;/g, "'")
    .replace(/\n{2,}/g, '\n')
    .trim();
}

function formatRelativeDate(dateStr: string): string {
  const d = new Date(dateStr);
  if (Number.isNaN(d.getTime())) return dateStr;

  const diffMs = Date.now() - d.getTime();
  if (diffMs < 0) return dateStr;

  const minutes = Math.floor(diffMs / 60000);
  if (minutes < 1) return 'just now';
  if (minutes < 60) return `${minutes} minute${minutes === 1 ? '' : 's'} ago`;

  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours} hour${hours === 1 ? '' : 's'} ago`;

  const days = Math.floor(hours / 24);
  if (days < 7) return `${days} day${days === 1 ? '' : 's'} ago`;

  const weeks = Math.floor(days / 7);
  if (weeks < 5) return `${weeks} week${weeks === 1 ? '' : 's'} ago`;

  const months = Math.floor(days / 30);
  if (months < 12) return `${months} month${months === 1 ? '' : 's'} ago`;

  const years = Math.floor(days / 365);
  return `${years} year${years === 1 ? '' : 's'} ago`;
}

function avatarColor(name: string): string {
  let hash = 0;
  for (let i = 0; i < name.length; i++) {
    hash = (hash * 31 + name.charCodeAt(i)) | 0;
  }
  return AVATAR_PALETTE[Math.abs(hash) % AVATAR_PALETTE.length];
}

function initials(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '?';
  if (parts.length === 1) return parts[0].charAt(0).toUpperCase();
  return (parts[0].charAt(0) + parts[parts.length - 1].charAt(0)).toUpperCase();
}

function GoogleMark({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path
        d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 01-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z"
        fill="#4285F4"
      />
      <path
        d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
        fill="#34A853"
      />
      <path
        d="M5.84 14.09A6.97 6.97 0 015.47 12c0-.72.13-1.43.37-2.09V7.07H2.18A11.96 11.96 0 001 12c0 1.94.46 3.77 1.18 5.07l3.66-2.98z"
        fill="#FBBC05"
      />
      <path
        d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 1.09 14.97 0 12 0 7.7 0 3.99 2.47 2.18 6.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
        fill="#EA4335"
      />
    </svg>
  );
}

function StarRow({
  rating,
  size = 16,
  className = '',
}: {
  rating: number;
  size?: number;
  className?: string;
}) {
  const filled = Math.round(rating);
  return (
    <span className={`reviews-stars ${className}`} aria-label={`${rating} out of 5 stars`}>
      {Array.from({ length: 5 }, (_, i) => (
        <svg
          key={i}
          width={size}
          height={size}
          viewBox="0 0 20 20"
          fill={i < filled ? STAR_YELLOW : '#DADCE0'}
          aria-hidden="true"
        >
          <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
        </svg>
      ))}
    </span>
  );
}

function ReviewCard({ review, index }: { review: Review; index: number }) {
  const [expanded, setExpanded] = useState(false);
  const name = hasValue(review.name) ? review.name.trim() : 'Resident';
  const rating = parseRating(review.rating);
  const rawText = hasValue(review.text) ? stripHtml(review.text) : '';
  const needsTruncate = rawText.length > TRUNCATE_LENGTH;
  const displayText =
    needsTruncate && !expanded ? `${rawText.slice(0, TRUNCATE_LENGTH).trimEnd()}…` : rawText;
  const relativeDate = hasValue(review.date) ? formatRelativeDate(review.date) : null;
  const source = hasValue(review.source) ? review.source.trim().toLowerCase() : 'google';
  const isGoogle = source.includes('google') || source === 'custom' || !hasValue(review.source);

  return (
    <article className="reviews-card" style={{ animationDelay: `${Math.min(index, 8) * 40}ms` }}>
      <header className="reviews-card__header">
        <span
          className="reviews-card__avatar"
          style={{ backgroundColor: avatarColor(name) }}
          aria-hidden="true"
        >
          {initials(name)}
        </span>
        <div className="reviews-card__meta">
          <p className="reviews-card__name">{name}</p>
          {relativeDate && <p className="reviews-card__date">{relativeDate}</p>}
        </div>
      </header>

      <StarRow rating={rating} size={18} className="reviews-card__stars" />

      {rawText && (
        <div className="reviews-card__body">
          <p>{displayText}</p>
          {needsTruncate && (
            <button
              type="button"
              className="reviews-card__more"
              onClick={() => setExpanded((v) => !v)}
              aria-expanded={expanded}
            >
              {expanded ? 'Read less' : 'Read more'}
            </button>
          )}
        </div>
      )}

      <footer className="reviews-card__source">
        {isGoogle ? (
          <>
            <GoogleMark className="reviews-card__source-icon" />
            <span>Posted on Google</span>
          </>
        ) : (
          <span>Posted on {review.source}</span>
        )}
      </footer>
    </article>
  );
}

export function Reviews() {
  const heading = useContentToken('reviews.heading') || 'What our customers say';
  const summaryLabel = useContentToken('reviews.summary_label') || 'Google Reviews';
  const googleCta = useSlotGroup('reviews.google_cta', googleCtaDef);

  const rawReviews = useCollectionBinding<Review>('reviews.items', {
    collection: 'reviews',
  });

  const reviews = useMemo(() => {
    if (!rawReviews) return null;
    return [...rawReviews].sort((a, b) => {
      const da = a.date ? new Date(a.date).getTime() : 0;
      const db = b.date ? new Date(b.date).getTime() : 0;
      return db - da;
    });
  }, [rawReviews]);

  const { average, count } = useMemo(() => {
    if (!reviews || reviews.length === 0) return { average: 0, count: 0 };
    const sum = reviews.reduce((acc, r) => acc + parseRating(r.rating), 0);
    return {
      average: Math.round((sum / reviews.length) * 10) / 10,
      count: reviews.length,
    };
  }, [reviews]);

  const sectionRef = useScrollReveal<HTMLElement>({ threshold: 0.08 });
  const averageLabel =
    Number.isInteger(average) && average > 0 ? average.toFixed(1) : String(average);
  const ctaHref = googleCta.url || '#';
  const ctaLabel = googleCta.text || 'Review us on Google';
  const showCta = hasValue(googleCta.url);

  return (
    <>
      <section ref={sectionRef} className="reviews-page" aria-label="Resident reviews">
        <div className="reviews-page__inner">
          <EditableToken path="reviews.heading" as="h2" className="reviews-page__heading">
            {heading}
          </EditableToken>

          <div className="reviews-summary">
            <div className="reviews-summary__left">
              <div className="reviews-summary__brand">
                <GoogleMark className="reviews-summary__g" />
                <EditableToken path="reviews.summary_label" as="span" className="reviews-summary__label">
                  {summaryLabel}
                </EditableToken>
              </div>
              <div className="reviews-summary__rating">
                <span className="reviews-summary__score">{count > 0 ? averageLabel : '—'}</span>
                <StarRow rating={count > 0 ? average : 0} size={20} />
                <span className="reviews-summary__count">({count})</span>
              </div>
            </div>

            <EditableSlotGroup groupPath="reviews.google_cta" definition={googleCtaDef}>
              {showCta ? (
                <a
                  href={ctaHref}
                  target={googleCta._target || '_blank'}
                  rel="noopener noreferrer"
                  className="reviews-summary__cta"
                >
                  {ctaLabel}
                </a>
              ) : (
                <span className="reviews-summary__cta reviews-summary__cta--muted">{ctaLabel}</span>
              )}
            </EditableSlotGroup>
          </div>

          <EditableCollectionBinding bindingPath="reviews.items" label="Reviews">
            {reviews && reviews.length > 0 ? (
              <div className="reviews-feed">
                {reviews.map((review, index) => (
                  <ReviewCard
                    key={hasValue(review.name) ? `${review.name}-${review.date ?? index}` : index}
                    review={review}
                    index={index}
                  />
                ))}
              </div>
            ) : (
              <div className="reviews-empty">
                <p>Resident reviews will be displayed here.</p>
              </div>
            )}
          </EditableCollectionBinding>
        </div>
      </section>

      <style>{`
        .reviews-page {
          background: #f7f7f7;
          padding: 2.5rem 1.25rem 3.5rem;
        }
        @media (min-width: 768px) {
          .reviews-page {
            padding: 3rem 2rem 4.5rem;
          }
        }

        .reviews-page__inner {
          max-width: 720px;
          margin: 0 auto;
        }

        .reviews-page__heading {
          margin: 0 0 1.75rem;
          text-align: center;
          font-family: var(--vibe-font-heading, Georgia, 'Times New Roman', serif);
          font-size: clamp(1.75rem, 3vw, 2.25rem);
          font-weight: 700;
          line-height: 1.25;
          color: var(--vibe-color-primary);
          letter-spacing: -0.01em;
        }

        .reviews-summary {
          display: flex;
          flex-wrap: wrap;
          align-items: center;
          justify-content: space-between;
          gap: 1rem 1.5rem;
          padding: 1.15rem 1.25rem;
          margin-bottom: 1.25rem;
          background: #ececec;
          border-radius: 12px;
        }

        .reviews-summary__left {
          display: flex;
          flex-direction: column;
          gap: 0.55rem;
          min-width: 0;
        }

        .reviews-summary__brand {
          display: inline-flex;
          align-items: center;
          gap: 0.5rem;
        }

        .reviews-summary__g {
          width: 22px;
          height: 22px;
          flex-shrink: 0;
        }

        .reviews-summary__label {
          font-family: var(--vibe-font-body, system-ui, sans-serif);
          font-size: 1.05rem;
          font-weight: 600;
          color: #202124;
        }

        .reviews-summary__rating {
          display: inline-flex;
          align-items: center;
          gap: 0.45rem;
          flex-wrap: wrap;
        }

        .reviews-summary__score {
          font-family: var(--vibe-font-body, system-ui, sans-serif);
          font-size: 1.35rem;
          font-weight: 700;
          color: #202124;
          line-height: 1;
        }

        .reviews-summary__count {
          font-family: var(--vibe-font-body, system-ui, sans-serif);
          font-size: 0.95rem;
          color: #5f6368;
        }

        .reviews-summary__cta {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          padding: 0.7rem 1.15rem;
          border-radius: 6px;
          background: ${CTA_BLUE};
          color: #fff;
          font-family: var(--vibe-font-body, system-ui, sans-serif);
          font-size: 0.9rem;
          font-weight: 600;
          line-height: 1.2;
          text-decoration: none;
          white-space: nowrap;
          transition: background 0.15s ease, transform 0.15s ease;
        }
        .reviews-summary__cta:hover {
          background: #1557b0;
        }
        .reviews-summary__cta:active {
          transform: translateY(1px);
        }
        .reviews-summary__cta--muted {
          background: #9aa0a6;
          cursor: default;
          pointer-events: none;
        }

        .reviews-stars {
          display: inline-flex;
          align-items: center;
          gap: 2px;
        }

        .reviews-feed {
          display: flex;
          flex-direction: column;
          gap: 1rem;
        }

        .reviews-card {
          background: #ececec;
          border-radius: 12px;
          padding: 1.25rem 1.25rem 1.1rem;
          animation: reviews-card-in 0.45s ease both;
        }

        @keyframes reviews-card-in {
          from {
            opacity: 0;
            transform: translateY(10px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        .reviews-card__header {
          display: flex;
          align-items: center;
          gap: 0.75rem;
          margin-bottom: 0.65rem;
        }

        .reviews-card__avatar {
          width: 42px;
          height: 42px;
          border-radius: 50%;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          color: #fff;
          font-family: var(--vibe-font-body, system-ui, sans-serif);
          font-size: 0.95rem;
          font-weight: 600;
          flex-shrink: 0;
        }

        .reviews-card__meta {
          min-width: 0;
        }

        .reviews-card__name {
          margin: 0;
          font-family: var(--vibe-font-body, system-ui, sans-serif);
          font-size: 0.95rem;
          font-weight: 600;
          color: #202124;
          line-height: 1.3;
        }

        .reviews-card__date {
          margin: 0.1rem 0 0;
          font-family: var(--vibe-font-body, system-ui, sans-serif);
          font-size: 0.8rem;
          color: #5f6368;
          line-height: 1.3;
        }

        .reviews-card__stars {
          margin-bottom: 0.65rem;
        }

        .reviews-card__body {
          margin-bottom: 0.85rem;
        }

        .reviews-card__body p {
          margin: 0;
          font-family: var(--vibe-font-body, system-ui, sans-serif);
          font-size: 0.925rem;
          line-height: 1.55;
          color: #3c4043;
          white-space: pre-wrap;
        }

        .reviews-card__more {
          margin-top: 0.35rem;
          padding: 0;
          border: 0;
          background: none;
          color: ${CTA_BLUE};
          font-family: var(--vibe-font-body, system-ui, sans-serif);
          font-size: 0.9rem;
          font-weight: 600;
          cursor: pointer;
        }
        .reviews-card__more:hover {
          text-decoration: underline;
        }

        .reviews-card__source {
          display: inline-flex;
          align-items: center;
          gap: 0.4rem;
          font-family: var(--vibe-font-body, system-ui, sans-serif);
          font-size: 0.8rem;
          color: #5f6368;
        }

        .reviews-card__source-icon {
          width: 14px;
          height: 14px;
        }

        .reviews-empty {
          padding: 3rem 1rem;
          text-align: center;
          color: #5f6368;
          font-family: var(--vibe-font-body, system-ui, sans-serif);
          font-size: 1.05rem;
        }

        @media (prefers-reduced-motion: reduce) {
          .reviews-card {
            animation: none;
          }
          .reviews-summary__cta {
            transition: none;
          }
        }
      `}</style>
    </>
  );
}

export const contentSchema: ComponentContentSchema = {
  slot: 'Reviews',
  displayName: 'Reviews',
  tokens: {
    'reviews.heading': {
      label: 'Page Heading',
      type: 'text',
      defaultValue: 'What our customers say',
    },
    'reviews.summary_label': {
      label: 'Summary Label',
      type: 'text',
      defaultValue: 'Google Reviews',
    },
  },
  slotGroups: {
    'reviews.google_cta': googleCtaDef,
  },
  collectionBindings: {
    'reviews.items': {
      label: 'Reviews',
      collection: 'reviews',
      description: 'Resident reviews shown in the Google-style feed',
    },
  },
};

(Reviews as unknown as { contentSchema: ComponentContentSchema }).contentSchema = contentSchema;

export default Reviews;
